// Single source of truth for where Prybar can take the user. Add a page here
// once and it becomes voice-navigable and self-describing; no server change.
// A guardrail test (navigation.test.ts) fails if a route here stops existing in
// the router, so a rename can't silently break navigation.
export interface PrybarPlace {
  /** Stable id the model can reference. */
  id: string;
  /** Router path. */
  route: string;
  /** Plain-language description sent to the model as [App context]. */
  label: string;
  /** Spoken synonyms that should resolve here. */
  aliases?: string[];
}

export const PRYBAR_PLACES: PrybarPlace[] = [
  {
    id: "today",
    route: "/",
    label:
      "your Today page: the moment asking for you, your Snapshot digest, and your weekly brief",

    aliases: ["home", "today"],
  },
  {
    id: "snapshot",
    route: "/snapshot",
    label:
      "your Snapshot: the wellbeing score broken down by behavior (food, movement, sleep, stress, connection)",
    aliases: ["score", "wellbeing score"],
  },

  {
    id: "explore",
    route: "/explore",
    label:
      "Explore: the hub of cards for Collection, Community, Challenges, Logbook, Anatomy and Connectors",
    aliases: ["explore", "hub"],
  },
  {
    id: "library",
    route: "/explore/collection",
    label: "Collection: recipes, articles, resets, stories, guides, and podcasts available to you",
    aliases: ["collection", "library", "content", "articles", "recipes"],
  },
  {
    id: "community",
    route: "/community",
    label: "your team community feed: posts, kudos, and this week's recipe",
    aliases: ["community", "team", "feed"],
  },
  {
    id: "connectors",
    route: "/connectors",
    label:
      "your connections: wearables, data sources, and AI assistants (connect to Claude, ChatGPT)",
    aliases: ["connections", "connectors", "integrations", "connect", "mcp", "claude", "assistant"],
  },
  {
    id: "settings",
    route: "/settings",
    label: "your settings: quiet hours, timezone, and profile",
    aliases: ["settings", "preferences"],
  },
];

// Important sections reachable from anywhere (cross-page). onScreen (below) only
// surfaces sections on the current screen; this list lets Prybar jump to a known
// section from another page. Each route points at an element that declares the
// matching data-prybar-target. Add an entry when a section should be reachable
// from anywhere; a same-screen-only section needs no entry (onScreen finds it).
export interface PrybarSection {
  id: string;
  route: string;
  label: string;
}

export const PRYBAR_SECTIONS: PrybarSection[] = [
  {
    id: "connect-ai",
    route: "/connectors#prybar=connect-ai",
    label: "connect an AI assistant (Claude, ChatGPT, or any MCP client) to HealthOS",
  },
  {
    id: "weekly-brief",
    // The brief opens as a modal over Today, so there is no anchor to scroll
    // to; the query the brief-ready push uses is the way in.
    route: "/today?brief=today",
    label: "your weekly brief on the Today page",
  },
];

export function describePlace(route: string): string {
  const path = route.split(/[?#]/)[0] ?? route;
  const place = PRYBAR_PLACES.find((p) =>
    p.route === "/" ? path === "/" : path === p.route || path.startsWith(`${p.route}/`),
  );
  return place?.label ?? "the HealthOS app";
}

export interface PrybarOnScreenTarget {
  /** The data-prybar-target value. */
  id: string;
  /** Human label (from data-prybar-label, aria-label, a heading, or the id). */
  label: string;
  /** Ready-to-use route the model passes to app.navigate to scroll here. */
  route: string;
}

// Live-discovered sections the bar can scroll to on the current screen. Any
// element that declares data-prybar-target shows up here automatically, so a new
// section is reachable the moment it is marked - no registry edit, no server change.
// Per-item targets (microsteps, content) are resolved by their own tools, so they
// are excluded here to keep the section list focused.
export function collectOnScreenTargets(): PrybarOnScreenTarget[] {
  if (typeof document === "undefined") return [];
  const path = window.location.pathname;
  const out: PrybarOnScreenTarget[] = [];
  const seen = new Set<string>();
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-prybar-target]"))) {
    const id = el.getAttribute("data-prybar-target");
    if (!id || seen.has(id) || /^(microstep|content)-/.test(id)) continue;
    seen.add(id);
    const label =
      el.getAttribute("data-prybar-label") ||
      el.getAttribute("aria-label") ||
      el.querySelector("h1, h2, h3")?.textContent?.trim() ||
      id;
    out.push({ id, label: label.slice(0, 80), route: `${path}#prybar=${id}` });
  }
  return out;
}
